import {
  ActiveSwitch,
  ProjectionInputCell,
  RankInputCell
} from '@library/components/modals/PlayerModal/NoContextPlayerModal/StatTable';
import type {
  BoardBuilderProjection,
  BoardBuilderUpdateProjection
} from '@library/types/boardBuilder';
import {
  INVALID_BOARD_TIME,
  INVALID_DISPLAY_TIME,
  INVALID_PROJECTION_VALUE,
  INVALID_START_TIME
} from '@bo-utils/boardBuilder';
import type {
  LeagueInfo,
  LeagueProjectionAttributes
} from '@library/types/leagues';
import {
  PlayerImage,
  PlayerName,
  PlayerProfile,
  PlayerSubheader
} from '@library/components/player';
import {
  PlayerModalProjectionBoardTimePicker,
  PlayerModalProjectionStartTimePicker
} from '@library/components/modals/PlayerModal/StatTable';
import {
  formatWithTimezoneChange,
  isTimeBefore
} from '@library/utils/dateTime';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { DisplayTimePicker } from '@library/components/simulations';
import type { FormattedGameMode } from '@library/types/gameModes';
import { MultiSelectFilter } from '@library/components/filters';
import { TradeSettingsProbabilityInput } from '@library/components/inputs';
import { getGameModeSelectOptions } from '@bo-utils/gameModes';
import { isStatusRemoved } from '@library/utils';
import { useGetAverageScoreMutation } from '@bo-services/projectionsApi';
import { useSplitFlag } from '@bo-hooks/useSplitFlag';

export interface BoardBuilderProjectionRowProps {
  confirmedGameModes: Array<FormattedGameMode>;
  durations: Array<LeagueProjectionAttributes>;
  leagueInfo: LeagueInfo;
  mainDurationId: string;
  onChange: (projection: BoardBuilderUpdateProjection) => void;
  projection: BoardBuilderProjection;
  isDisplayTimeVisible: boolean;
}

export const BoardBuilderProjectionRow = memo(
  ({
    confirmedGameModes,
    durations,
    leagueInfo,
    mainDurationId,
    onChange,
    projection,
    isDisplayTimeVisible
  }: BoardBuilderProjectionRowProps) => {
    const [initialProjection] = useState<BoardBuilderProjection>(projection);
    const [getAverageScore, { isSuccess, data, isLoading, isUninitialized }] =
      useGetAverageScoreMutation();

    const {
      autoTrade,
      boardTime,
      description,
      durationId,
      errors,
      gameModeIds,
      inGame,
      isPromo,
      maxProbabilityOfMore,
      minProbabilityOfMore,
      oddsType,
      optIn = false,
      player: {
        image_url: imageUrl,
        name,
        foreign_id: foreignId,
        player_id: playerId
      },
      projectionId,
      projectionValue,
      rank,
      rowStatus,
      startTime,
      displayTime,
      statType,
      statTypeId,
      status,
      targetProbabilityOfMore
    } = projection;

    const { isActive: isAFXActive } = useSplitFlag({
      flagName: 'AUTO_TRADER_ENABLED'
    });

    useEffect(() => {
      if (!isLoading && isSuccess && data && data[0]?.score_avg) {
        onChange({
          durations,
          key: 'averageScore',
          initialProjection,
          leagueInfo,
          mainDurationId,
          projectionId,
          rowStatus,
          value: Number(data[0]?.score_avg)
        });
      }
    }, [
      data,
      durations,
      initialProjection,
      isUninitialized,
      isSuccess,
      isLoading,
      leagueInfo,
      mainDurationId,
      onChange,
      projectionId,
      rowStatus
    ]);

    const handleChange = useCallback(
      ({
        key,
        value
      }: {
        key: string;
        value:
          | boolean
          | number
          | Array<number>
          | string
          | Array<string>
          | undefined;
      }) => {
        onChange({
          durations,
          key,
          initialProjection,
          leagueInfo,
          mainDurationId,
          projectionId,
          rowStatus,
          value
        });

        if (key === 'projectionValue' && isUninitialized && value) {
          getAverageScore([
            {
              duration_id: Number(durationId),
              foreign_id: foreignId,
              player_id: Number(playerId),
              stat_type_id: Number(statTypeId)
            }
          ]).catch(() => {});
        }
      },
      [
        onChange,
        durations,
        initialProjection,
        leagueInfo,
        mainDurationId,
        projectionId,
        rowStatus,
        isUninitialized,
        getAverageScore,
        durationId,
        foreignId,
        playerId,
        statTypeId
      ]
    );

    const handleChangeTime = useCallback(
      ({
        key,
        value
      }: {
        key: 'startTime' | 'boardTime' | 'displayTime';
        value: DateTime;
      }) => {
        handleChange({
          key,
          value: value.toISO() ?? ''
        });
      },
      [handleChange]
    );

    const handleStartTimeChange = useCallback(
      (value: DateTime) => handleChangeTime({ key: 'startTime', value }),
      [handleChangeTime]
    );

    const handleBoardTimeChange = useCallback(
      (value: DateTime) => handleChangeTime({ key: 'boardTime', value }),
      [handleChangeTime]
    );

    const handleDisplayTimeChange = useCallback(
      (value: DateTime) => handleChangeTime({ key: 'displayTime', value }),
      [handleChangeTime]
    );

    const gameModeOptions = useMemo(
      () => getGameModeSelectOptions(confirmedGameModes),
      [confirmedGameModes]
    );

    const [selectedGameIds, setSelectedGameIds] = useState<Array<number>>([]);

    const gameModeValues = gameModeIds?.length
      ? gameModeIds
          .filter((id) => {
            const gameMode = gameModeOptions.find(
              (option) => Number(option.value) === id
            );

            if (gameMode?.name.toLowerCase() === 'culture') {
              return selectedGameIds.includes(Number(gameMode.value));
            }

            return true;
          })
          .map((id) => String(id))
      : [];

    const handleGameModeChange = (values: Array<string>) => {
      const selectedGameModeIds = values.filter((id) => {
        const numId = Number(id);
        const gameMode = gameModeOptions.find(
          (option) => Number(option.value) === numId
        );
        return (
          gameMode?.name.toLowerCase() !== 'culture' ||
          selectedGameIds.includes(Number(gameMode.value))
        );
      });

      const isCultureSelected = values.some((value) => {
        const gameMode = gameModeOptions.find(
          (option) => option.value === value
        );
        return gameMode?.name.toLowerCase() === 'culture';
      });

      if (isCultureSelected) {
        const cultureGameModeId = gameModeOptions.find(
          (option) => option.name.toLowerCase() === 'culture'
        )?.value;
        if (
          cultureGameModeId &&
          !selectedGameModeIds.includes(cultureGameModeId)
        ) {
          selectedGameModeIds.push(cultureGameModeId);
        }
      }

      setSelectedGameIds(selectedGameModeIds.map(Number));

      handleChange({
        key: 'gameModeIds',
        value: selectedGameModeIds
      });
    };

    const startDateTime = DateTime.fromISO(startTime);
    const startDateTimeDisplayValue = formatWithTimezoneChange(
      startDateTime,
      'ccc t ZZZZ'
    );
    const playerDescription = `vs. ${description} • ${startDateTimeDisplayValue}`;
    const isInvalidProjectionValue = errors?.includes(INVALID_PROJECTION_VALUE);
    const isInvalidBoardTime = errors?.includes(INVALID_BOARD_TIME);
    const isInvalidStartTime = errors?.includes(INVALID_START_TIME);
    const isInvalidDisplayTime = errors?.includes(INVALID_DISPLAY_TIME);
    const isRemoved = isStatusRemoved(status);
    const isPastStartTime = isTimeBefore({ dateValue: startDateTime });
    const isProjectionValueLocked = isPastStartTime || isPromo;

    return (
      <tr className="border-soFresh-140 w-full border-t">
        <td className="py-4">
          <PlayerProfile
            className="flex h-full min-w-52 shrink-0 basis-52 items-center gap-4 self-stretch pl-4"
            name={name}
            imageUrl={imageUrl}
            subheader={playerDescription}
          >
            <PlayerImage className="bg-soFresh-120 size-[49px] rounded-lg" />
            <div className="flex flex-col">
              <PlayerName className="font-heebo text-soClean-100 block text-sm font-medium leading-tight tracking-tight" />
              <PlayerSubheader className="text-soClean-160 font-heebo block text-xs font-medium leading-[19px] tracking-tight" />
            </div>
          </PlayerProfile>
        </td>
        <ProjectionInputCell
          className="py-6"
          afx={isAFXActive}
          autoTrade={autoTrade}
          autoTradeDisabled={isProjectionValueLocked}
          disabled={
            isProjectionValueLocked || (autoTrade && rowStatus !== 'NEW')
          }
          inGame={inGame}
          inGameDisabled={isProjectionValueLocked}
          initialValue={initialProjection.projectionValue}
          isInvalidProjectionValue={isInvalidProjectionValue}
          isPromo={isPromo}
          oddsType={oddsType}
          onAutoTradeChange={() =>
            handleChange({ key: 'autoTrade', value: !autoTrade })
          }
          onInGameChange={() => handleChange({ key: 'inGame', value: !inGame })}
          onChange={(value?: string) =>
            handleChange({
              key: 'projectionValue',
              value: value ? Number(value) : ''
            })
          }
          optIn={optIn}
          projectionId={projectionId}
          value={projectionValue}
        />
        <PlayerModalProjectionBoardTimePicker
          boardTime={boardTime}
          disabled={isProjectionValueLocked}
          dateError={isInvalidBoardTime}
          handleChange={handleBoardTimeChange}
          initialBoardTime={initialProjection.boardTime}
          startTime={startTime}
          status={status}
        />
        <PlayerModalProjectionStartTimePicker
          dateError={isInvalidStartTime}
          disabled={isProjectionValueLocked}
          handleChange={handleStartTimeChange}
          initialStartTime={projection.startTime}
          startTime={startTime}
          status={status}
        />
        {isDisplayTimeVisible && (
          <DisplayTimePicker
            dateError={isInvalidDisplayTime}
            disabled={isProjectionValueLocked}
            handleChange={handleDisplayTimeChange}
            initialDisplayTime={projection.displayTime}
            displayTime={displayTime}
            status={status}
          />
        )}
        <td className="py-4">
          <div className="flex justify-center">
            <MultiSelectFilter
              customHeight="h-8"
              disabled={false}
              onChange={(value) => handleGameModeChange(value)}
              options={gameModeOptions}
              label="Game Modes"
              selectedValues={gameModeValues}
              testId="board-builder-game-modes-filter"
              portal
            />
          </div>
        </td>
        <RankInputCell
          className="py-6 text-center"
          disabled={isProjectionValueLocked}
          handleProjectionRankChange={(value) =>
            handleChange({ key: 'rank', value: (value ?? '').toString() })
          }
          initialRank={initialProjection.rank}
          projectionId={projectionId}
          rank={rank}
        />
        <TradeSettingsProbabilityInput
          disabled={isProjectionValueLocked}
          probabilityType="minProbabilityOfMore"
          onValueChange={handleChange}
          value={minProbabilityOfMore}
        />
        <TradeSettingsProbabilityInput
          disabled={isProjectionValueLocked}
          probabilityType="targetProbabilityOfMore"
          onValueChange={handleChange}
          value={targetProbabilityOfMore}
        />
        <TradeSettingsProbabilityInput
          disabled={isProjectionValueLocked}
          probabilityType="maxProbabilityOfMore"
          onValueChange={handleChange}
          value={maxProbabilityOfMore}
        />
        <ActiveSwitch
          active={!isRemoved}
          disabled={false}
          handleChange={(active: boolean) =>
            handleChange({
              key: 'status',
              value: active ? 'pre_game' : 'removed'
            })
          }
          statType={`${foreignId}-${statType}`}
          status={status}
        />
      </tr>
    );
  }
);
